import { Injectable } from '@angular/core';
import { DataPointsApi } from 'src/app/api/proxy/datapoints/data-points-api';
import { consumerDimensionGet } from 'src/app/api/proxy/datapoints/fn/consumer/consumer-dimension-get';
import { consumerDimensionPost } from 'src/app/api/proxy/datapoints/fn/consumer/consumer-dimension-post';
import { MemberProfileDimensionModel } from 'src/app/api/proxy/datapoints/models/member-profile-dimension-model';
import { EventService } from '../events/event.service';
import { PromptService } from '../promtp-service/prompt.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { SecureStorageService } from '../storage/secure-storage.service';
import { TokenService } from '../token/token.service';
import { hasDimensionSetupChanged } from './dimensions-setup.util';

@Injectable({
  providedIn: 'root'
})
export class DimensionsService {
  private readonly CACHE_TTL_MS = 60 * 60 * 1000;

  constructor(
    private _dataPointsApi: DataPointsApi,
    private _promptService: PromptService,
    private _tokenService: TokenService,
  ) {
    // Clear cache on logout only. Do not fetch on login here — ProfileDataRefresh /
    // home bootstrap load dimensions after a valid token is available.
    EventService.userUpdated.subscribe((user) => {
      if (!user) {
        void this.clearCache();
      }
    });
  }

  /**
   * Load consumer dimensions from cache when fresh; otherwise fetch and cache for 1 hour.
   * Publishes the resolved dimension set via profileDimensionsUpdated.
   */
  public async getDimensions(forceRefresh = false): Promise<MemberProfileDimensionModel[]> {
    const cached = await this.getCachedDimensions();
    const lastRefresh = await SecureStorageService.get(SecureStorageKey.LastProfileDimensionsRefresh);

    if (!forceRefresh && cached && this.isCacheFresh(lastRefresh)) {
      return this.publishDimensionsUpdate(cached);
    }

    if (!(await this.hasAccessToken())) {
      return this.publishDimensionsUpdate(cached ?? []);
    }

    if (!navigator.onLine) {
      return this.publishDimensionsUpdate(cached ?? []);
    }

    const fresh = await this.fetchDimensionsFromApi();
    if (fresh) {
      return await this.applyFreshDimensionsFromServer(fresh, cached);
    }

    return this.publishDimensionsUpdate(cached ?? []);
  }

  public async clearCache(): Promise<void> {
    await SecureStorageService.remove(SecureStorageKey.ProfileDimensions);
    await SecureStorageService.remove(SecureStorageKey.LastProfileDimensionsRefresh);
    this.publishDimensionsUpdate([]);
  }

  /**
   * Save consumer dimensions and refresh the local cache.
   */
  public async saveDimensions(dimensions: { key: string; value: string | null | undefined }[]): Promise<boolean> {
    if (!(await this.hasAccessToken())) {
      return false;
    }

    if (!navigator.onLine) {
      return false;
    }

    try {
      await this._dataPointsApi.invoke(consumerDimensionPost, {
        body: { dimensions }
      });

      const fresh = await this.fetchDimensionsFromApi();
      if (fresh) {
        const cached = await this.getCachedDimensions();
        await this.applyFreshDimensionsFromServer(fresh, cached);
      } else {
        await this.mergeSavedDimensionsIntoCache(dimensions);
      }

      return true;
    } catch (error) {
      console.error('Error saving dimensions', error);
      this._promptService.showError('Error saving dimensions');
      return false;
    }
  }

  private publishDimensionsUpdate(dimensions: MemberProfileDimensionModel[]): MemberProfileDimensionModel[] {
    const normalized = this.normalizeDimensions(dimensions);
    EventService.profileDimensionsUpdated.emit(normalized);
    return normalized;
  }

  private async fetchDimensionsFromApi(): Promise<MemberProfileDimensionModel[] | null> {
    if (!(await this.hasAccessToken())) {
      return null;
    }

    try {
      return await this._dataPointsApi.invoke(consumerDimensionGet, {}) as MemberProfileDimensionModel[];
    } catch (error) {
      console.error('Error loading dimensions', error);
      const status = (error as { status?: number })?.status;
      // Auth failures during login/registration race — never surface a blocking popup.
      if (status === 401 || status === 403) {
        return null;
      }
      if (await this.hasAccessToken()) {
        this._promptService.showError('Error loading dimensions');
      }
      return null;
    }
  }

  /** True only when a non-expired access token is available (refreshes if needed). */
  private async hasAccessToken(): Promise<boolean> {
    const token = await this._tokenService.getTokenAsync();
    return !!token;
  }

  private normalizeDimensions(dimensions: MemberProfileDimensionModel[]): MemberProfileDimensionModel[] {
    const unique: MemberProfileDimensionModel[] = [];
    const seenKeys = new Set<string>();

    for (const dim of dimensions || []) {
      if (dim.key && !seenKeys.has(dim.key)) {
        seenKeys.add(dim.key);
        unique.push(dim);
      }
    }

    return unique.sort((a, b) => this.compareByOrderNumber(a, b));
  }

  private compareByOrderNumber(a: MemberProfileDimensionModel, b: MemberProfileDimensionModel): number {
    const aOrder = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.orderNumber ?? Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return (a.key || '').localeCompare(b.key || '');
  }

  private async getCachedDimensions(): Promise<MemberProfileDimensionModel[] | null> {
    const cached = await SecureStorageService.get(SecureStorageKey.ProfileDimensions);
    if (!cached) {
      return null;
    }

    try {
      return JSON.parse(cached) as MemberProfileDimensionModel[];
    } catch {
      return null;
    }
  }

  private async applyFreshDimensionsFromServer(
    fresh: MemberProfileDimensionModel[],
    previous: MemberProfileDimensionModel[] | null,
  ): Promise<MemberProfileDimensionModel[]> {
    const normalizedFresh = this.normalizeDimensions(fresh);
    const normalizedPrevious = previous ? this.normalizeDimensions(previous) : null;
    if (hasDimensionSetupChanged(normalizedPrevious, normalizedFresh)) {
      EventService.profileDimensionsSetupChanged.emit();
    }
    return await this.setCachedDimensions(fresh);
  }

  private async setCachedDimensions(dimensions: MemberProfileDimensionModel[]): Promise<MemberProfileDimensionModel[]> {
    const normalized = this.normalizeDimensions(dimensions);
    await SecureStorageService.set(SecureStorageKey.ProfileDimensions, JSON.stringify(normalized));
    await SecureStorageService.set(SecureStorageKey.LastProfileDimensionsRefresh, Date.now().toString());
    return this.publishDimensionsUpdate(normalized);
  }

  private isCacheFresh(lastRefresh: string | null): boolean {
    if (!lastRefresh) {
      return false;
    }

    const parsed = parseInt(lastRefresh, 10);
    if (Number.isNaN(parsed)) {
      return false;
    }

    return Date.now() - parsed < this.CACHE_TTL_MS;
  }

  private async mergeSavedDimensionsIntoCache(dimensions: { key: string; value: string | null | undefined }[]): Promise<void> {
    const cached = await this.getCachedDimensions();
    if (!cached) {
      return;
    }

    const savedByKey = new Map(dimensions.map((dim) => [dim.key, dim.value]));
    const updated = cached.map((dim) => {
      if (!dim.key || !savedByKey.has(dim.key)) {
        return dim;
      }

      return { ...dim, value: savedByKey.get(dim.key) };
    });

    await this.setCachedDimensions(updated);
  }
}
